import type {
  AddBalancePostingsCommandData,
  BalancePostingSpec,
} from '@festina/life-general-types/life'
import { notEmpty, typeUtils } from '@festina-life/core'
import {
  useFormikContext,
  useTranslate,
  useTranslateEnum,
} from '@festina-life/ui/hooks'
import {
  createColumnDefiner,
  SortableTableSearch,
} from '@festina-life/ui/listsAndTables'
import { useField } from 'formik'
import { useState } from 'react'

import { useAmountFormatter } from '../../../hooks/useAmountFormatter'
import { useRowActions } from '../../../hooks/useRowActions'
import { useTranslateCover } from '../../../hooks/useTranslateCover'
import { useHook } from '../../../lenteHooks'
import { BalancePostingSpecModal } from './BalancePostingSpecModal'
import { BalancePostingSpecTableAction } from './BalancePostingSpecTableAction'

const defineColumn = createColumnDefiner<BalancePostingSpec>()

const validateKey = typeUtils.createValidator<AddBalancePostingsCommandData>()

export const BalancePostingSpecsTable = () => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const renderPolicy = useHook('pages.commitment.useLazyRenderPolicy')()
  const translateCover = useTranslateCover()
  const { formatWithDecimals } = useAmountFormatter()

  const [editing, setEditing] = useState<BalancePostingSpec>()

  const [{ value }, , { setValue }] = useField<
    AddBalancePostingsCommandData['balancePostingSpecs']
  >(validateKey('balancePostingSpecs'))

  const {
    status: { shouldFieldsBeDisabled },
  } = useFormikContext()

  const handleAddBalancePostingSpec = (
    balancePostingSpec: BalancePostingSpec,
  ) => setValue((value ?? []).concat(balancePostingSpec))

  const handleEditBalancePostingSpec = async (
    balancePostingSpec: BalancePostingSpec,
  ) => {
    await setValue(
      value?.map((spec) => (spec === editing ? balancePostingSpec : spec)),
    )
    setEditing(undefined)
  }

  const handleDeleteBalancePostingSpec = (
    balancePostingSpec: BalancePostingSpec,
  ) => setValue(value?.filter((spec) => spec !== balancePostingSpec))

  const columns = {
    accountType: defineColumn({
      heading: translate('action.addBalancePosting.field.accountType'),
      keyPath: 'accountType',
      formatter: (x) => translateEnum('life.enum.AccountType', x),
    }),
    amount: defineColumn({
      heading: translate('action.addBalancePosting.field.amount'),
      keyPath: 'amount',
      formatter: formatWithDecimals,
      alignment: 'right',
    }),
    coverKey: defineColumn({
      heading: translate('action.addBalancePosting.field.coverKey'),
      keyPath: 'coverKey',
      formatter: (coverKey) => translateCover(coverKey),
    }),
    policeKey: defineColumn({
      heading: translate('action.addBalancePosting.field.policeKey'),
      keyPath: 'policeKey', // translation typo intentionally matches field name
      formatter: renderPolicy,
    }),
    postingType: defineColumn({
      heading: translate('action.addBalancePosting.field.postingType'),
      keyPath: 'postingType',
      formatter: (x) => translateEnum('life.enum.PostingType', x),
    }),
  }

  const rowActions = useRowActions<BalancePostingSpec>((row) => ({
    onEdit: () => setEditing(row),
    onDelete: () => handleDeleteBalancePostingSpec(row),
  }))

  const data = value?.filter(notEmpty) ?? []

  return (
    <>
      {editing && (
        <BalancePostingSpecModal
          initialValues={editing}
          onSubmit={handleEditBalancePostingSpec}
          onClose={() => setEditing(undefined)}
        />
      )}
      <SortableTableSearch
        title={translate('action.addBalancePosting.field.balancePostingSpecs')}
        columns={columns}
        data={data}
        actions={
          shouldFieldsBeDisabled ? undefined : (
            <BalancePostingSpecTableAction
              onSubmit={handleAddBalancePostingSpec}
            />
          )
        }
        rowActions={shouldFieldsBeDisabled ? undefined : rowActions}
      />
    </>
  )
}
